import {useIntl, defineMessages} from 'react-intl'
import {SIZES} from '../../constants'
import {useBrand} from '../../contexts/brand-context'

const messages = defineMessages({
    sizeYouOrdered: {defaultMessage: 'Size You Ordered', id: 'pdp_reviews.label.size_you_ordered'},
    sizeYouOrderedLabel: {defaultMessage: 'Select', id: 'pdp_reviews.label.size_you_ordered_label'},
    reviewTitle: {defaultMessage: 'Review title', id: 'pdp_reviews.label.review_title'},
    reviewTextGarage: {defaultMessage: 'Your Review', id: 'pdp_reviews.label.review_text.garage'},
    reviewTextDynamite: {defaultMessage: 'Your Review', id: 'pdp_reviews.label.review_text.dynamite'},
    userNickname: {defaultMessage: 'Nickname', id: 'pdp_reviews.label.user_nickname'},
    userEmail: {defaultMessage: 'Email Address (optional)', id: 'pdp_reviews.label.user_email'},
    height: {defaultMessage: 'Height (optional)', id: 'pdp_reviews.label.height'},
    weight: {defaultMessage: 'Approx. Weight (optional)', id: 'pdp_reviews.label.weight'}
})

export default function useWriteReviewFields({
    reviewFormData,
    form: {
        control,
        formState: { errors = {} } = {}
    } = {},
    prefix = ''
}) {
    const {formatMessage} = useIntl()
    const {isGarage} = useBrand()

    const fields = {
        [reviewFormData?.additionalfield_sizeOrdered?.Id]: {
            name: `${prefix}${reviewFormData?.additionalfield_sizeOrdered?.Id}`,
            label: formatMessage(messages.sizeYouOrdered),
            defaultValue: '',
            type: 'select',
            options: SIZES,
            error: errors[`${prefix}${reviewFormData?.additionalfield_sizeOrdered?.Id}`],
            control,
            placeholder: formatMessage(messages.sizeYouOrderedLabel),
            inputProps: {
                maxLength: reviewFormData?.additionalfield_sizeOrdered?.MaxLength
            }
        },
        [reviewFormData?.title?.Id]: {
            name: `${prefix}${reviewFormData?.title?.Id}`,
            label: formatMessage(messages.reviewTitle),
            defaultValue: '',
            type: 'text',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Review title is required.',
                          id: 'pdp_reviews.required.review_title.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Review title is required.',
                          id: 'pdp_reviews.required.review_title.dynamite'
                      }),
                maxLength: {
                    value: 50,
                    message: formatMessage({
                        defaultMessage: 'Maximum of 50 characters.',
                        id: 'pdp_reviews.max_length.review_title'
                    })
                }
            },
            error: errors[`${prefix}${reviewFormData?.title?.Id}`],
            control,
            placeholder: 'Review title',
            hideLabel: true
        },
        [reviewFormData?.reviewtext?.Id]: {
            name: `${prefix}${reviewFormData?.reviewtext?.Id}`,
            label: isGarage
                ? formatMessage(messages.reviewTextGarage)
                : formatMessage(messages.reviewTextDynamite),
            defaultValue: '',
            type: 'text',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Review text is required.',
                          id: 'pdp_reviews.required.review_text.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Review text is required.',
                          id: 'pdp_reviews.required.review_text.dynamite'
                      }),
                minLength: {
                    value: 50,
                    message: formatMessage({
                        defaultMessage: 'Minimum of 50 characters.',
                        id: 'pdp_reviews.min_length.review_text'
                    })
                },
                maxLength: {
                    value: 250,
                    message: formatMessage({
                        defaultMessage: 'Maximum of 250 characters.',
                        id: 'pdp_reviews.max_length.review_text'
                    })
                }
            },
            error: errors[`${prefix}${reviewFormData?.reviewtext?.Id}`],
            control,
            placeholder: isGarage
                ? formatMessage(messages.reviewTextGarage)
                : formatMessage(messages.reviewTextDynamite),
            hideLabel: true
        },
        [reviewFormData?.usernickname?.Id]: {
            name: `${prefix}${reviewFormData?.usernickname?.Id}`,
            label: formatMessage(messages.userNickname),
            defaultValue: '',
            type: 'text',
            rules: {
                required: isGarage
                    ? formatMessage({
                          defaultMessage: 'Review nickname is required.',
                          id: 'pdp_reviews.required.review_usernickname.garage'
                      })
                    : formatMessage({
                          defaultMessage: 'Review nickname is required.',
                          id: 'pdp_reviews.required.review_usernickname.dynamite'
                      }),
                minLength: {
                    value: 2,
                    message: formatMessage({
                        defaultMessage: 'Minimum of 2 characters.',
                        id: 'pdp_reviews.min_length.review_usernickname'
                    })
                },
                maxLength: {
                    value: 25,
                    message: formatMessage({
                        defaultMessage: 'Maximum of 25 characters.',
                        id: 'pdp_reviews.max_length.review_usernickname'
                    })
                }
            },
            error: errors[`${prefix}${reviewFormData?.usernickname?.Id}`],
            control,
            placeholder: formatMessage(messages.userNickname),
            hideLabel: true
        },
        [reviewFormData?.useremail?.Id]: {
            name: `${prefix}${reviewFormData?.useremail?.Id}`,
            label: formatMessage(messages.userEmail),
            defaultValue: '',
            type: 'email',
            rules: {
                maxLength: {
                    value: 255,
                    message: formatMessage({
                        defaultMessage: 'Maximum of 255 characters.',
                        id: 'pdp_reviews.max_length.review_useremail'
                    })
                }
            },
            error: errors[`${prefix}${reviewFormData?.useremail?.Id}`],
            control,
            placeholder: formatMessage(messages.userEmail),
            hideLabel: true
        },
        [reviewFormData?.contextdatavalue_height?.Id]: {
            name: `${prefix}${reviewFormData?.contextdatavalue_height?.Id}`,
            label: formatMessage(messages.height),
            defaultValue: '',
            type: 'select',
            error: errors[`${prefix}${reviewFormData?.contextdatavalue_height?.Id}`],
            control,
            placeholder: 'Height',
            hideLabel: true
        },
        [reviewFormData?.contextdatavalue_weight?.Id]: {
            name: `${prefix}${reviewFormData?.contextdatavalue_weight?.Id}`,
            label: formatMessage(messages.weight),
            defaultValue: '',
            type: 'select',
            error: errors[`${prefix}${reviewFormData?.contextdatavalue_weight?.Id}`],
            control,
            placeholder: 'Weight',
            hideLabel: true
        },
        [reviewFormData?.agreedtotermsandconditions.Id]: {
            name: `${prefix}${reviewFormData?.agreedtotermsandconditions.Id}`,
            label: '',
            defaultValue: true,
            type: 'checkbox'
        }
    }

    return fields
}
